<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { useI18n } from "vue-i18n";

import { maskCurrency } from "@/common/utils/mask";

import type { PlanType, PlanPayloadType } from "@/common/types/plan/plan";

import { baseInput, baseSelect, baseTextarea } from "@/common/components";

const { t, tm } = useI18n();

const emit = defineEmits(["update:data", "validate"]);

const props = defineProps<{
	data: PlanType | null;
	isPatch?: boolean;
	isEdit?: boolean;
}>();

const DEFAULT_DATA = {
	stripe_product_id: "",
	name: "",
	status: "ACTIVE",
	free: false,
	description: null,
};

const payload = ref<PlanPayloadType>({ ...DEFAULT_DATA });
const errors = ref<{ [key: string]: string }>({});

const statusOptions = computed(() => [
	{ option: t("common.labels.active"), value: "ACTIVE" },
	{ option: t("common.labels.inactive"), value: "INACTIVE" },
]);

const freeOptions = computed(() => [
	{ option: t("common.labels.yes"), value: true },
	{ option: t("common.labels.not"), value: false },
]);

function validate() {
	const free = ["stripe_product_id", "name", "status"];

	Object.keys(payload.value).forEach((key) => {
		delete errors.value[key];

		if (!payload.value[key] && !free.includes(key))
			errors.value[key] = t("error.required");
	});

	emit("validate", Object.keys(errors.value).length === 0);
}

function update(key: keyof typeof payload.value) {
	validate();

	if (props.isPatch) {
		if (payload.value[key] === props.data[key]) return;

		emit("update:data", { [key]: payload.value[key] });
		return;
	}

	emit("update:data", payload.value);
}

function resumeData() {
	if (!props.data) return;

	const { stripe_product_id, name, status, free, description } = props.data;

	payload.value = {
		...payload.value,
		stripe_product_id,
		name,
		status,
		free,
		description,
	};
}

watch(
	() => props.data,
	() => {
		resumeData();
	},
	{ immediate: true },
);
</script>

<template>
	<article class="form-plan">
		<section class="group-name">
			<base-input
				v-model="payload.name"
				:value="payload.name"
				:placeholder="t('common.placeholders.name')"
				required
				maxlength="75"
				@blur="update('name')"
			>
				<template #label> {{ t("common.labels.name") }} *</template>
				<template v-if="errors.name" #error> {{ errors.name }} </template>
			</base-input>

			<base-select
				v-model="payload.status"
				:value="payload.status"
				:options="statusOptions"
				:is-empty="false"
				@change="update('status')"
			>
				<template #label> {{ t("common.labels.status") }}</template>
			</base-select>
		</section>

		<section class="flex flex-row gap-4">
			<base-input
				v-model="payload.stripe_product_id"
				:value="payload.stripe_product_id"
				@blur="update('stripe_product_id')"
			>
				<template #label> Stripe Produto ID *</template>
				<template v-if="errors.stripe_product_id" #error>
					{{ errors.stripe_product_id }}
				</template>
			</base-input>

			<base-select
				v-model="payload.free"
				:value="payload.free"
				:options="freeOptions"
				:is-empty="false"
				@change="update('free')"
			>
				<template #label> {{ t("common.labels.free") }}</template>
			</base-select>
		</section>

		<base-textarea
			v-model="payload.description"
			:value="payload.description"
			:rows="5"
			@blur="update('description')"
		>
			<template #label>
				{{ t("common.labels.description") }}
			</template>
		</base-textarea>
	</article>
</template>

<style lang="scss" scoped>
.form-plan {
	display: flex;
	flex-direction: column;
	width: 100%;
	gap: 1rem;

	.group-name {
		display: grid;
		grid-template-columns: 2fr 1fr;
		gap: 1rem;
	}
}
</style>
